import { ISchedule } from '../models/company.model';

/* -------------------------------------------------------------------------- */
/*                      ANCHOR Horario por día (varios turnos)                 */
/* -------------------------------------------------------------------------- */

export const DAYS = [
  { day: 1, label: 'Lunes', short: 'Lun' },
  { day: 2, label: 'Martes', short: 'Mar' },
  { day: 3, label: 'Miércoles', short: 'Mié' },
  { day: 4, label: 'Jueves', short: 'Jue' },
  { day: 5, label: 'Viernes', short: 'Vie' },
  { day: 6, label: 'Sábado', short: 'Sáb' },
  { day: 7, label: 'Domingo', short: 'Dom' },
];

export interface IRange { opensAt: string; closesAt: string; }
export interface IDaySchedule { day: number; open: boolean; ranges: IRange[]; }

export function toDays(schedule: ISchedule[] | null | undefined): IDaySchedule[] {
  return DAYS.map(d => {
    const ranges = (schedule || [])
      .filter(s => s.dayOfWeek === d.day)
      .sort((a, b) => a.position - b.position)
      .map(s => ({ opensAt: s.opensAt.slice(0, 5), closesAt: s.closesAt.slice(0, 5) }));
    return { day: d.day, open: ranges.length > 0, ranges: ranges.length ? ranges : [{ opensAt: '09:00', closesAt: '18:00' }] };
  });
}

export function fromDays(days: IDaySchedule[]): ISchedule[] {
  const out: ISchedule[] = [];
  for (const d of days) {
    if (!d.open) continue;
    d.ranges.forEach((r, i) => out.push({ dayOfWeek: d.day, opensAt: r.opensAt, closesAt: r.closesAt, position: i }));
  }
  return out;
}

export function defaultDays(): IDaySchedule[] {
  return DAYS.map(d => ({
    day: d.day,
    open: d.day <= 6,
    ranges: [{ opensAt: d.day === 6 ? '09:00' : '08:00', closesAt: d.day === 6 ? '14:00' : '20:00' }],
  }));
}

const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };

/** Errores por día: cierre > apertura y sin traslapes. */
export function dayErrors(d: IDaySchedule): string | null {
  if (!d.open) return null;
  const sorted = [...d.ranges].sort((a, b) => toMin(a.opensAt) - toMin(b.opensAt));
  for (const r of sorted) {
    if (!r.opensAt || !r.closesAt) return 'Completa la hora de apertura y cierre.';
    if (toMin(r.closesAt) <= toMin(r.opensAt)) return 'La hora de cierre debe ser después de la apertura.';
  }
  for (let i = 1; i < sorted.length; i++) {
    if (toMin(sorted[i].opensAt) < toMin(sorted[i - 1].closesAt)) return 'Los horarios no se pueden traslapar.';
  }
  return null;
}

export function rangesLabel(ranges: IRange[]): string {
  return ranges.map(r => `${r.opensAt} – ${r.closesAt}`).join(' · ');
}

/** Resumen compacto: «Lun a Jue · 08:00 – 20:00». */
export function scheduleSummary(days: IDaySchedule[]): { label: string; value: string }[] {
  const out: { label: string; value: string }[] = [];
  let i = 0;
  while (i < days.length) {
    const value = days[i].open ? rangesLabel(days[i].ranges) : 'Cerrado';
    let j = i;
    while (j + 1 < days.length && (days[j + 1].open ? rangesLabel(days[j + 1].ranges) : 'Cerrado') === value) j++;
    const a = DAYS[i].short;
    const b = DAYS[j].short;
    out.push({ label: i === j ? a : j === i + 1 ? `${a} y ${b}` : `${a} a ${b}`, value });
    i = j + 1;
  }
  return out;
}

/** «Abierto ahora · cierra 20:00» calculado con la zona horaria del negocio. */
export function openStatus(days: IDaySchedule[], timeZone: string, now = new Date()): { open: boolean; label: string } {
  let weekday = 1;
  let minutes = 0;
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: timeZone || 'America/Mexico_City', weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(now);
    const wd = parts.find(p => p.type === 'weekday')?.value || 'Mon';
    weekday = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(wd) + 1;
    minutes = (Number(parts.find(p => p.type === 'hour')?.value) % 24) * 60 + Number(parts.find(p => p.type === 'minute')?.value);
  } catch { /* zona inválida */ }
  const today = days.find(d => d.day === weekday);
  if (today?.open) {
    const current = today.ranges.find(r => toMin(r.opensAt) <= minutes && minutes < toMin(r.closesAt));
    if (current) return { open: true, label: `Abierto ahora · cierra ${current.closesAt}` };
    const next = today.ranges.find(r => toMin(r.opensAt) > minutes);
    if (next) return { open: false, label: `Cerrado · abre ${next.opensAt}` };
  }
  return { open: false, label: 'Cerrado ahora' };
}

export function todayIndex(timeZone: string): number {
  try {
    const wd = new Intl.DateTimeFormat('en-US', { timeZone: timeZone || 'America/Mexico_City', weekday: 'short' }).format(new Date());
    return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(wd) + 1;
  } catch { return 1; }
}

export const TIME_OPTIONS: string[] = (() => {
  const out: string[] = [];
  for (let h = 0; h < 24; h++) for (const m of [0, 15, 30, 45]) out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  out.push('23:59');
  return out;
})();
